import React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Users as UsersIcon } from "lucide-react";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";

import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth.jsx";

const ROLES = ["viewer", "presenter", "builder", "admin"];

function formatDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default function UsersPage() {
  const { user: me, hasPermission } = useAuth();
  const queryClient = useQueryClient();
  const canManage = hasPermission("users.manage");

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["users"],
    queryFn: async () => (await api.get("/users")).data,
    enabled: canManage,
  });

  const updateMutation = useMutation({
    mutationFn: async ({ userId, role }) => {
      const { data } = await api.patch(`/users/${userId}`, { role });
      return data;
    },
    onSuccess: () => {
      toast.success("Role updated");
      queryClient.invalidateQueries({ queryKey: ["users"] });
    },
    onError: (err) => toast.error(formatApiError(err)),
  });

  if (!canManage) {
    return (
      <div
        className="rounded-lg border border-amber-200 bg-amber-50 p-6 text-sm text-amber-800"
        data-testid="users-no-access"
      >
        You need the <span className="font-semibold">admin</span> role to view this page.
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="users-page">
      <header>
        <div className="flex items-center gap-3">
          <div className="grid h-9 w-9 place-items-center rounded-md bg-indigo-50 text-indigo-700">
            <UsersIcon size={18} />
          </div>
          <div>
            <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Users</h1>
            <p className="mt-1 text-sm text-slate-500">
              Manage who can access the agent and what they can do.
            </p>
          </div>
        </div>
      </header>

      {isLoading ? (
        <Skeleton className="h-40 w-full rounded-xl" data-testid="users-loading" />
      ) : isError ? (
        <div
          className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700"
          data-testid="users-error"
        >
          {formatApiError(error)}
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
          <Table data-testid="users-table">
            <TableHeader className="bg-slate-50">
              <TableRow>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500">User</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500">Role</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500">Status</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500">Last login</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500">Created</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(data || []).map((u) => {
                const isSelf = u.email === me?.email;
                return (
                  <TableRow key={u.user_id} data-testid={`user-row-${u.email}`}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <Avatar src={u.picture} name={u.name || u.email} />
                        <div>
                          <div className="text-sm font-medium text-slate-900">
                            {u.name || "—"}
                            {isSelf && (
                              <span className="ml-2 rounded-full bg-indigo-50 px-2 py-0.5 text-[10px] font-medium text-indigo-700">
                                you
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-slate-500">{u.email}</div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Select
                        value={u.role}
                        onValueChange={(v) =>
                          updateMutation.mutate({ userId: u.user_id, role: v })
                        }
                        disabled={isSelf}
                      >
                        <SelectTrigger
                          className="w-36"
                          data-testid={`role-select-${u.email}`}
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {ROLES.map((r) => (
                            <SelectItem key={r} value={r} className="capitalize">
                              {r}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {isSelf && (
                        <p className="mt-1 text-[10px] text-slate-400">
                          You can't change your own role.
                        </p>
                      )}
                    </TableCell>
                    <TableCell>
                      <span
                        className={[
                          "inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-medium",
                          u.status === "active"
                            ? "bg-emerald-50 text-emerald-700"
                            : "bg-slate-100 text-slate-600",
                        ].join(" ")}
                        data-testid={`user-status-${u.email}`}
                      >
                        <span
                          className={[
                            "h-1.5 w-1.5 rounded-full",
                            u.status === "active" ? "bg-emerald-500" : "bg-slate-400",
                          ].join(" ")}
                        />
                        {u.status}
                      </span>
                    </TableCell>
                    <TableCell className="text-sm text-slate-600">{formatDate(u.last_login_at)}</TableCell>
                    <TableCell className="text-sm text-slate-600">{formatDate(u.created_at)}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

function Avatar({ src, name }) {
  if (src) {
    return (
      <img
        src={src}
        alt={name}
        className="h-9 w-9 rounded-full border border-slate-200 object-cover"
      />
    );
  }
  const initials = (name || "?")
    .split(/\s+/)
    .map((s) => s[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
  return (
    <div className="grid h-9 w-9 place-items-center rounded-full bg-indigo-100 text-xs font-semibold text-indigo-700">
      {initials}
    </div>
  );
}
